import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import AppColor from "../../utilities/AppColor";
import { makeStyles } from '@material-ui/core';
import AppButton from 'components/CustomButtons/AppButton';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: '0.5rem 1rem',
    backgroundColor: AppColor.mainColor,
    color: AppColor.white,
    display: 'flex',
    justifyContent: 'space-between'
  },
  title: {
    textTransform: 'capitalize',
    fontWeight: '600'
  },
  whiteColor: {
    color: AppColor.white
  },
  closeButton: {
    color: theme.palette.grey[500],
    padding: '0.5rem'
  }
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, closeIconClass, closeTitleClass, titleComponentClass, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classNames([classes.root, titleComponentClass])} {...other}>
      <Typography className={classNames([classes.whiteColor, classes.title, closeTitleClass])} variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon className={classNames([classes.whiteColor, closeIconClass])} />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const AppDialog = (props) => {
  const {
    onClose,
    open,
    title,
    children,
    actions,
    noTitle,
    maxWidth = 'xs',
    closeIconClass,
    closeTitleClass,
    contentClass
  } = props;

  const useStyles = makeStyles(() => ({
    titleComponentClass: {
      alignItems: "center",
      '& h6': {
        flex: '1 auto'
      }
    }
  }));

  const classes = useStyles();
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
      return false;
    }
    if (reason === "escapeKeyDown") {
      return false;
    }
    if (typeof onClose === "function") {
      onClose();
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={true}
    >
      {
        !noTitle && (
          <DialogTitle
            id="customized-dialog-title"
            onClose={onClose}
            closeIconClass={closeIconClass}
            closeTitleClass={closeTitleClass}
            titleComponentClass={classes.titleComponentClass}
          >
            {title}
          </DialogTitle>
        )
      }
      <DialogContent dividers className={contentClass}>
        {children}
      </DialogContent>
      {actions && (
        <DialogActions>
          {actions.map(({ label, onPress, buttonColor = AppColor.mainColor, mode, disabled }, index) => (
            <AppButton
              key={index}
              autoFocus
              onClick={onPress}
              buttonColor={buttonColor}
              mode={mode}
              label={label}
              disabled={disabled}
            />
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
}

AppDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  children: PropTypes.any,
  noTitle: PropTypes.bool,
  maxWidth: PropTypes.string,
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    onPress: PropTypes.func
  })),
  closeIconClass: PropTypes.any,
  closeTitleClass: PropTypes.any,
  titleComponentClass: PropTypes.any,
  contentClass: PropTypes.any
}

export default AppDialog;
